import React, { useState, useRef } from 'react';
import { Play, Upload, X } from 'lucide-react';

const VideoBlock = ({ block, onUpdate }) => {
  const [urlInput, setUrlInput] = useState(block.url || '');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const extractVideoId = (url) => {
    if (!url) return null;

    const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^\s&]+)/;
    const match = url.match(youtubeRegex);
    return match?.[1];
  };

  const handleAddVideo = () => {
    if (urlInput.trim()) {
      onUpdate(block.id, { url: urlInput });
      setUrlInput('');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        onUpdate(block.id, {
          url: event.target?.result,
          fileName: file.name,
          isLocalFile: true
        });
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading video:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const videoId = extractVideoId(block.url);
  const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  const isLocalVideo = block.isLocalFile && block.url;

  return (
    <div className="video-block">
      {block.url ? (
        <div className="video-preview">
          {embedUrl ? (
            <iframe
              width="100%"
              height="300"
              src={embedUrl}
              title={block.title || 'Video'}
              frameBorder="0"
              allowFullScreen
            />
          ) : isLocalVideo ? (
            <video
              width="100%"
              height="300"
              controls
              src={block.url}
              title={block.title || 'Video'}
            />
          ) : (
            <div className="video-url-display">
              <p>{block.url}</p>
            </div>
          )}
          <div className="video-meta">
            <input
              type="text"
              placeholder="Video title (optional)"
              value={block.title || ''}
              onChange={(e) => onUpdate(block.id, { title: e.target.value })}
              className="video-title-input"
            />
            <button
              onClick={() => onUpdate(block.id, { url: '', title: '', isLocalFile: false })}
              className="remove-video-btn"
            >
              <X size={16} />
              Remove Video
            </button>
          </div>
        </div>
      ) : (
        <div className="video-input">
          <Play size={32} />
          <p>Add a video to your article</p>
          <div className="video-upload-options">
            <div className="upload-area">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="upload-btn"
              >
                <Upload size={16} />
                {isUploading ? 'Uploading...' : 'Upload Video'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <small>MP4, WebM, Ogg supported</small>
            </div>
            <div className="divider">OR</div>
            <div className="video-url-input">
              <input
                type="url"
                placeholder="Paste YouTube, Vimeo, or video URL..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddVideo()}
                className="url-field"
              />
              <button onClick={handleAddVideo} className="add-video-btn">
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoBlock;
